import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { useAuthStore } from '@/stores/auth'
import { ApiResponse, ApiError } from '@/types'
import { translate as t } from '@/plugins/i18n'
import { addBreadcrumb, captureException } from '@/plugins/sentry'

class ApiService {
  private axiosInstance: AxiosInstance
  private baseURL: string

  constructor() {
    // Use environment variable for API URL
    this.baseURL = import.meta.env.VITE_API_BASE_URL || 'https://api.matt.com.co/api'
    
    console.log('API Service initialized with baseURL:', this.baseURL)
    
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors(): void {
    // Request interceptor to add auth token
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const authStore = useAuthStore()
        if (authStore.accessToken) {
          config.headers.Authorization = `Bearer ${authStore.accessToken}`
          console.log('API Request with auth token:', config.method?.toUpperCase(), config.url, 'Token present:', !!authStore.accessToken)
        } else {
          console.warn('API Request without auth token:', config.method?.toUpperCase(), config.url)
        }
        return config
      },
      (error) => {
        return Promise.reject(this.handleError(error))
      }
    )

    // Response interceptor to handle token refresh
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config
        const status = error.response?.status
        console.error('API Response error:', status, error.response?.data)

        // Drop a breadcrumb for any non-2xx so it shows up on the next Sentry event.
        addBreadcrumb({
          category: 'http',
          type: 'http',
          level: status && status >= 500 ? 'error' : 'warning',
          data: {
            method: originalRequest?.method?.toUpperCase(),
            url: originalRequest?.url,
            status_code: status ?? 0,
          },
        })

        // Don't retry auth-related requests to avoid infinite loops
        const isAuthRequest = originalRequest.url?.includes('/auth/refresh/') ||
                             originalRequest.url?.includes('/auth/logout/')

        if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
          originalRequest._retry = true

          const authStore = useAuthStore()
          console.log('Attempting token refresh due to', error.response?.status, 'error')

          try {
            const refreshed = await authStore.refreshAuthToken()

            if (refreshed && authStore.accessToken) {
              originalRequest.headers.Authorization = `Bearer ${authStore.accessToken}`
              console.log('Token refreshed, retrying request')
              return this.axiosInstance(originalRequest)
            } else {
              // refreshAuthToken() already cleared auth data if tokens were invalid.
              // For network errors it intentionally kept auth data. Just throw.
              throw this.handleError(error)
            }
          } catch (refreshError: any) {
            console.error('Token refresh error:', refreshError)
            // refreshAuthToken() doesn't throw — this only handles unexpected JS errors.
            // Don't clear auth here; we don't know why it failed.
            throw this.handleError(error)
          }
        }

        // If this is a failed refresh token request, clear auth immediately
        if (originalRequest.url?.includes('/auth/refresh/') && (error.response?.status === 401 || error.response?.status === 403)) {
          console.log('Refresh token request failed - tokens expired, clearing auth')
          const authStore = useAuthStore()
          await authStore.clearAuthData()
        }

        // Send 5xx and network errors to Sentry — these are the ones we always want to see.
        // Skip 4xx (validation/auth flows) to keep the issues list clean.
        const isServerError = typeof status === 'number' && status >= 500
        const isNetworkError = !error.response && !!error.request
        if (isServerError || isNetworkError) {
          captureException(error, {
            method: originalRequest?.method?.toUpperCase(),
            url: originalRequest?.url,
            status_code: status ?? 0,
          })
        }

        return Promise.reject(this.handleError(error))
      }
    )
  }

  private handleError(error: any): ApiError {
    
    const errorDetails = {
      hasResponse: !!error.response,
      hasRequest: !!error.request,
      message: error.message,
      config: error.config ? {
        url: error.config.url,
        method: error.config.method,
        baseURL: error.config.baseURL,
        headers: error.config.headers
      } : null,
      response: error.response ? {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      } : null
    }
    console.error('API Error Details:', JSON.stringify(errorDetails, null, 2))

    if (error.response) {
      // Server responded with error status
      // Extract error code from various possible locations
      const errorCode = error.response.data?.code ||
                       error.response.data?.error?.code ||
                       'SERVER_ERROR'

      return {
        code: errorCode,
        message: error.response.data?.detail ||
                error.response.data?.error?.message ||
                error.response.data?.message ||
                t('errors.general'),
        details: error.response.data?.error?.details ||
                error.response.data?.details ||
                error.response.data?.messages,
        status: error.response.status
      }
    } else if (error.request) {
      // Network error
      return {
        code: 'NETWORK_ERROR',
        message: t('errors.network'),
        status: 0
      }
    } else {
      // Other error
      return {
        code: 'UNKNOWN_ERROR',
        message: error.message || t('errors.generic'),
        status: 0
      }
    }
  }

  // Generic HTTP methods
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.get(url, config)
      // Django returns data directly, so we wrap it in ApiResponse format
      return {
        data: response.data,
        error: null
      }
    } catch (error) {
      throw error
    }
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      console.log('Making POST request to:', `${this.baseURL}${url}`, 'with data:', JSON.stringify(data))
      const response: AxiosResponse<T> = await this.axiosInstance.post(url, data, config)
      console.log('POST request successful:', response.status, response.statusText)
      // Django returns data directly, so we wrap it in ApiResponse format
      return {
        data: response.data,
        error: null
      }
    } catch (error) {
      console.error('POST request failed:', error instanceof Error ? error.message : String(error))
      throw error
    }
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.put(url, data, config)
      // Django returns data directly, so we wrap it in ApiResponse format
      return {
        data: response.data,
        error: null
      }
    } catch (error) {
      throw error
    }
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.patch(url, data, config)
      // Django returns data directly, so we wrap it in ApiResponse format
      return {
        data: response.data,
        error: null
      }
    } catch (error) {
      throw error
    }
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.delete(url, config)
      // Django returns data directly, so we wrap it in ApiResponse format
      return {
        data: response.data,
        error: null
      }
    } catch (error) {
      throw error
    }
  }

  // File upload method
  async uploadFile<T>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.post(url, formData, {
        ...config,
        headers: {
          ...config?.headers,
          'Content-Type': 'multipart/form-data',
        },
      })
      // Django returns data directly, so we wrap it in ApiResponse format
      return {
        data: response.data,
        error: null
      }
    } catch (error) {
      throw error
    }
  }

  // Method to update base URL (for different environments)
  updateBaseURL(newBaseURL: string): void {
    this.baseURL = newBaseURL
    this.axiosInstance.defaults.baseURL = newBaseURL
  }

  // Method to get current base URL
  getBaseURL(): string {
    return this.baseURL
  }
}

// Export singleton instance
export const apiService = new ApiService()
export default apiService